/**
 * The twelve animals and their unlock rules.
 *
 * Pure data: no Phaser imports. Each animal unlocks on reaching a level OR a
 * star total, whichever comes first (CLAUDE.md).
 */

import type { AnimalState } from '../types';
import type { CosmeticId } from '../systems/TideRun';

export type AnimalId =
  | 'emperor-penguin'
  | 'arctic-fox'
  | 'adelie-penguin'
  | 'arctic-hare'
  | 'weddell-seal'
  | 'snow-petrel'
  | 'leopard-seal'
  | 'orca'
  | 'wandering-albatross'
  | 'humpback-whale'
  | 'elephant-seal'
  | 'polar-bear';

export interface AnimalDef {
  readonly id: AnimalId;
  readonly name: string;
  /** Levels completed needed to unlock. 0 means available from the start. */
  readonly unlockLevel: number;
  /** Total stars needed to unlock, as the alternative route. */
  readonly unlockStars: number;
  /**
   * The animal's own colour, used when no swatch is picked.
   *
   * Body art is drawn in white and greys so a tint can recolour it, which means
   * "natural" has to be a colour too - otherwise every animal would default to
   * a white blob rather than looking like itself.
   */
  readonly naturalTint: number;
  /** One friendly line for the select screen. */
  readonly blurb: string;
}

export const ANIMALS: readonly AnimalDef[] = [
  {
    id: 'emperor-penguin',
    name: 'Emperor Penguin',
    unlockLevel: 0,
    unlockStars: 0,
    naturalTint: 0x2f3a4a, // deep slate, like a wet black back in low sun
    blurb: 'The tallest penguin of all, and a champion slider.',
  },
  {
    id: 'arctic-fox',
    name: 'Arctic Fox',
    unlockLevel: 3,
    unlockStars: 6,
    naturalTint: 0xcfd8e3, // soft blue-grey, the arctic fox's "blue" coat
    blurb: 'Fluffy tail, fuzzy cheeks, and ears that hear under the snow.',
  },
  {
    id: 'adelie-penguin',
    name: 'Adelie Penguin',
    unlockLevel: 5,
    unlockStars: 10,
    naturalTint: 0x23272e, // almost black
    blurb: 'Small, speedy, and never still for a second.',
  },
  {
    id: 'arctic-hare',
    name: 'Arctic Hare',
    unlockLevel: 7,
    unlockStars: 18,
    naturalTint: 0xfbf8f4, // snow-bunny white
    blurb: 'A snowy bunny with big bouncy feet. Born to jump!',
  },
  {
    id: 'weddell-seal',
    name: 'Weddell Seal',
    unlockLevel: 10,
    unlockStars: 25,
    naturalTint: 0x6e7a8a, // soft grey
    blurb: 'Sings long bubbly songs under the ice.',
  },
  {
    id: 'snow-petrel',
    name: 'Snow Petrel',
    unlockLevel: 20,
    unlockStars: 50,
    naturalTint: 0xf7fbff, // snow white
    blurb: 'Pure white wings against a pure white sky.',
  },
  {
    id: 'leopard-seal',
    name: 'Leopard Seal',
    unlockLevel: 30,
    unlockStars: 80,
    naturalTint: 0x5a6472, // steel grey
    blurb: 'Spotty, curious, and a very strong swimmer.',
  },
  {
    id: 'orca',
    name: 'Orca',
    unlockLevel: 40,
    unlockStars: 110,
    naturalTint: 0x1e232b, // glossy black
    blurb: 'Travels in family pods and talks in clicks.',
  },
  {
    id: 'wandering-albatross',
    name: 'Wandering Albatross',
    unlockLevel: 55,
    unlockStars: 150,
    naturalTint: 0xeef2f7, // pale ivory
    blurb: 'The widest wings of any bird on Earth.',
  },
  {
    id: 'humpback-whale',
    name: 'Humpback Whale',
    unlockLevel: 70,
    unlockStars: 190,
    naturalTint: 0x3a4654, // blue grey
    blurb: 'Breaches clear out of the water, just because.',
  },
  {
    id: 'elephant-seal',
    name: 'Elephant Seal',
    unlockLevel: 85,
    unlockStars: 240,
    naturalTint: 0x7a6a5c, // muddy brown
    blurb: 'Enormous, snoozy, and surprisingly quick.',
  },
  {
    id: 'polar-bear',
    name: 'Polar Bear',
    unlockLevel: 100,
    unlockStars: 290,
    naturalTint: 0xf3eee0, // creamy white, a shade warmer than snow
    blurb: 'A visitor from the far north, with paws as big as dinner plates.',
  },
];

export const DEFAULT_ANIMAL_ID: AnimalId = 'emperor-penguin';

export const ANIMAL_IDS: readonly AnimalId[] = ANIMALS.map((animal) => animal.id);

export function animalById(id: string): AnimalDef | undefined {
  return ANIMALS.find((animal) => animal.id === id);
}

export function isAnimalId(value: string): value is AnimalId {
  return ANIMALS.some((animal) => animal.id === value);
}

/**
 * Animals that were replaced, old id to new. Saves are read through this, so a
 * player who had unlocked, picked or coloured the old animal keeps all of that
 * on its replacement instead of silently losing it.
 */
const REPLACED_ANIMALS: Readonly<Record<string, AnimalId>> = {
  'antarctic-krill': 'polar-bear',
};

/** The current id for `value`, following any replacement. */
export function currentAnimalId(value: string): string {
  return REPLACED_ANIMALS[value] ?? value;
}

export interface ColorSwatch {
  readonly name: string;
  readonly value: number;
  /** Earned in Tide Run; locked until the profile has it (docs/TIDE_RUN.md). */
  readonly cosmetic?: CosmeticId;
}

/**
 * Ten swatches shared by every animal, then four earned in Tide Run, plus
 * "natural" which clears the tint. Only the body layer is ever tinted, so
 * details stay readable (CLAUDE.md). Saves store the index, so new swatches
 * only ever go on the end.
 */
export const COLOR_SWATCHES: readonly ColorSwatch[] = [
  { name: 'Sky', value: 0x8ecae6 },
  { name: 'Teal', value: 0x2ec4b6 },
  { name: 'Mint', value: 0xa8e6cf },
  { name: 'Sunshine', value: 0xffd166 },
  { name: 'Mango', value: 0xff9f45 },
  { name: 'Coral', value: 0xff8fa3 },
  { name: 'Berry', value: 0xc77dff },
  { name: 'Lavender', value: 0xb8c0ff },
  { name: 'Moss', value: 0x9bc53d },
  { name: 'Cocoa', value: 0xd4a373 },
  { name: 'Ocean', value: 0x1f78c8, cosmetic: 'swatch-ocean' },
  { name: 'Coral Reef', value: 0xff6f61, cosmetic: 'swatch-coral-reef' },
  { name: 'Gold', value: 0xe0a91b, cosmetic: 'swatch-gold' },
  { name: 'Aurora', value: 0x3ddc97, cosmetic: 'swatch-aurora' },
];

/** Swatch index -> colour, or null when the animal's natural colour applies. */
export function swatchColor(index: number | undefined): number | null {
  if (index === undefined) return null;
  return COLOR_SWATCHES[index]?.value ?? null;
}

/** The colour to tint an animal's body with: the chosen swatch, else its own. */
export function bodyTintFor(animal: AnimalDef, swatchIndex: number | undefined): number {
  return swatchColor(swatchIndex) ?? animal.naturalTint;
}

/** Whichever comes first: the level, or the star total. */
export function isAnimalUnlocked(
  animal: AnimalDef,
  levelsCompleted: number,
  totalStars: number,
): boolean {
  if (animal.unlockLevel <= 0) return true;
  return levelsCompleted >= animal.unlockLevel || totalStars >= animal.unlockStars;
}

/** Every animal available at this much progress, in display order. */
export function unlockedAnimalsFor(levelsCompleted: number, totalStars: number): AnimalId[] {
  return ANIMALS.filter((animal) => isAnimalUnlocked(animal, levelsCompleted, totalStars)).map(
    (animal) => animal.id,
  );
}

/** "Reach level 20 or 50 stars" text for a locked card. */
export function unlockHintFor(animal: AnimalDef): string {
  return `Reach level ${animal.unlockLevel} or ${animal.unlockStars} stars`;
}

/* ------------------------------------------------------------------ frames */

/**
 * Frames per animation state (docs/ART_SPEC.md).
 *
 * Data, not rendering, so it lives here: the contact-sheet script and the
 * export tooling need it without pulling Phaser into Node.
 */
export const ANIMAL_FRAME_COUNTS: Readonly<Record<AnimalState, number>> = {
  idle: 2,
  crouch: 1,
  jump: 1,
  fall: 1,
  land: 1,
  cheer: 2,
  oops: 1,
};

export type AnimalLayer = 'body' | 'details';

/**
 * Art styles. 'cute' (scripts/art/animals_cute.py) is the game's look; the
 * original 'classic' set is kept for comparison behind ?style=classic.
 */
export type AnimalArtStyle = 'classic' | 'cute';
export const CUTE_STYLE_ANIMALS: ReadonlySet<string> = new Set([
  'emperor-penguin',
  'arctic-fox',
  'adelie-penguin',
  'arctic-hare',
  'weddell-seal',
  'snow-petrel',
  'leopard-seal',
  'orca',
  'wandering-albatross',
  'humpback-whale',
  'elephant-seal',
  'polar-bear',
]);

/** Drawn only in the cute style, so they use it whichever style is chosen. */
const CUTE_ONLY_ANIMALS: ReadonlySet<string> = new Set(['arctic-fox', 'arctic-hare']);

let artStyle: AnimalArtStyle = 'cute';

/** Chosen once at startup, before any animal texture key is built. */
export function setAnimalArtStyle(style: AnimalArtStyle): void {
  artStyle = style;
}

export function animalFrameKey(
  id: string,
  state: AnimalState,
  frame: number,
  layer: AnimalLayer,
): string {
  const cute = CUTE_ONLY_ANIMALS.has(id) || (artStyle === 'cute' && CUTE_STYLE_ANIMALS.has(id));
  const folder = cute ? `cute/${id}` : id;
  return `animals/${folder}/${state}_${frame}_${layer}`;
}

/** Every asset key one animal needs, so Preload can register them all. */
export function animalAssetKeys(id: string): string[] {
  const keys: string[] = [];
  for (const [state, count] of Object.entries(ANIMAL_FRAME_COUNTS)) {
    for (let frame = 0; frame < count; frame += 1) {
      keys.push(animalFrameKey(id, state as AnimalState, frame, 'body'));
      keys.push(animalFrameKey(id, state as AnimalState, frame, 'details'));
    }
  }
  return keys;
}
